import type {
  ConnectorModuleElement,
  ConnectorStripElement,
  EvidenceCardElement,
  LineTrendCardElement,
} from '../../../demo-elements/types'
import type { DemoChatScript } from '../../../types'

const ASSET = 'Primary Baler #2 — Bollegraaf HBC-110'

const STRIP_ID = 'strip-baler2'

function strip(
  connected: ('SCADA' | 'MPower' | 'OIS' | 'Warranty/Lot')[],
  probability: number | null,
  confidence: ConnectorStripElement['prediction']['confidence'],
  failureMode?: string,
  failureWindow?: string,
): ConnectorStripElement {
  return {
    kind: 'connector-strip',
    id: STRIP_ID,
    asset: ASSET,
    sources: (['SCADA', 'MPower', 'OIS', 'Warranty/Lot'] as const).map(
      (name) => ({
        name,
        connected: connected.includes(name),
      }),
    ),
    prediction: {
      probability,
      confidence,
      failureMode,
      failureWindow,
    },
  }
}

function evidence(
  id: string,
  source: EvidenceCardElement['source'],
  title: string,
  body: string,
): EvidenceCardElement {
  return { kind: 'evidence-card', id, source, title, body }
}

const mpowerConnector: ConnectorModuleElement = {
  kind: 'connector-module',
  id: 'connect-mpower',
  source: 'MPower',
  accent: 'mpower',
  description: 'Work-order history, parts inventory, technician roster.',
  stripUpdate: strip(
    ['SCADA', 'MPower'],
    55,
    'Low',
    'Hydraulic anomaly — possible seal',
  ),
  evidence: {
    kind: 'evidence-card',
    id: 'evidence-mpower-1',
    source: 'MPower',
    title: '47K runtime hours · last seal kit 14 months ago',
    body: "HBC-110 seal kits typically replace at 12–18 months under this load. This one's at the back of that window. Feb 14 inspection was clean — visual checks can't catch the cycle drift SCADA is showing.",
  },
  onConnectCue: {
    kind: 'text-block',
    id: 'cue-after-mpower',
    text: 'Probability moved to **55%**. The drift could be a seal — runtime hours line up. Need operator context to be sure. **Connect OIS next.**',
  },
}

const oisConnector: ConnectorModuleElement = {
  kind: 'connector-module',
  id: 'connect-ois',
  source: 'OIS',
  accent: 'ois',
  description: 'Operator event log — "slow cycle" notes, misfeeds, stops.',
  stripUpdate: strip(
    ['SCADA', 'MPower', 'OIS'],
    68,
    'Medium',
    'Hydraulic seal degradation (probable)',
  ),
  evidence: {
    kind: 'evidence-card',
    id: 'evidence-ois-1',
    source: 'OIS',
    title: 'Operators have flagged this 3 times in a week',
    body: 'M. Rodriguez · "slow cycle" · May 11. T. Johnson · same note · May 13. Wire-feed misfeed yesterday — third this month.',
  },
  onConnectCue: {
    kind: 'text-block',
    id: 'cue-after-ois',
    text: '**68%**, confidence to Medium. Three independent signals now — telemetry, runtime, operators. **One more — connect warranty/lot to see if this signature has shown up elsewhere in your network.**',
  },
}

const warrantyConnector: ConnectorModuleElement = {
  kind: 'connector-module',
  id: 'connect-warranty',
  source: 'Warranty/Lot',
  accent: 'warranty',
  description: 'Equipment lot data + cross-network failure history.',
  stripUpdate: strip(
    ['SCADA', 'MPower', 'OIS', 'Warranty/Lot'],
    78,
    'High',
    'Hydraulic seal degradation',
    'May 22 – May 25, 2026',
  ),
  evidence: {
    kind: 'evidence-card',
    id: 'evidence-network-1',
    source: 'Network',
    title: '91% match against 3 prior HBC-110 seal failures',
    body: 'Indianapolis, Fort Worth, Dallas — same model, same drift signature in the weeks before failure. Window: 7–10 days out from where this asset is now.',
  },
  onConnectCue: {
    kind: 'text-block',
    id: 'cue-after-warranty',
    text: "**Probability increased to 78%, high confidence.** Hydraulic seal degradation, window May 22–25. The picture's complete — two actions queued below, one for today and one for Thursday.",
  },
}

const CYCLE_TIME_TREND: LineTrendCardElement = {
  kind: 'line-trend',
  id: 'trend-cycle-time',
  title: 'Primary Baler #2 — cycle time (last 7 days)',
  yLabel: 'Avg cycle time (seconds)',
  unit: 's',
  yMin: 36,
  yMax: 42,
  baseline: { value: 38.5, label: '60-day baseline · 38.5s' },
  emphasizeLast: 2,
  points: [
    { x: 'May 8', y: 38.5 },
    { x: 'May 9', y: 38.7 },
    { x: 'May 10', y: 39.1 },
    { x: 'May 11', y: 39.6 },
    { x: 'May 12', y: 40.2 },
    { x: 'May 13', y: 40.8 },
    { x: 'May 14', y: 41.0 },
  ],
}

/**
 * Act 2 — Predictive maintenance with the data-source toggle.
 *
 * Beat 1: SCADA only → 42% Low confidence. Beat 2 (after pause): connect
 * MPower, OIS, Warranty/Lot one at a time, prediction sharpens to 78% High
 * with a specific failure mode and window. Two approval cards finalize the
 * use case.
 *
 * The visual heart of the act is the inline ConnectorStrip element — pills
 * light up as sources connect, probability rolls up, evidence cards slide
 * in. The demoer narrates over it.
 */
export const predictiveMaintenance: DemoChatScript = {
  id: 'predictive-maintenance',
  name: 'Predictive maintenance (with data-source toggle)',
  description:
    "Act 2. Predict a specific failure at a specific asset, then show prediction quality sharpen as you connect more of Republic's existing data sources.",
  triggers: [
    'primary baler 2',
    'baler 2',
    'diagnose baler 2',
    "what's wrong with primary baler 2",
    'baler 2 atlanta',
    'predictive maintenance',
  ],
  events: [
    // ─────────── Beat 1: SCADA-only diagnosis ───────────
    {
      type: 'status',
      delayMs: 200,
      message: 'Reading SCADA telemetry for Primary Baler #2...',
    },
    { type: 'thinking-start', delayMs: 200 },
    {
      type: 'thinking-delta',
      delayMs: 400,
      delta:
        'Currently connected: SCADA only. MPower, OIS, and warranty/lot data sources are not enabled. Running a SCADA-only failure-risk model.\n',
    },
    {
      type: 'thinking-delta',
      delayMs: 300,
      delta:
        'Cycle time trending up, hydraulic pressure variance elevated. Confidence will be capped without cross-source correlation.\n',
    },
    { type: 'thinking-stop', delayMs: 200 },
    { type: 'tool-call', delayMs: 200, name: 'query_scada' },
    {
      type: 'text-delta',
      delayMs: 500,
      delta:
        'Primary Baler #2. SCADA only right now — your other systems are sitting on the side.',
    },
    {
      type: 'demo-element-add',
      delayMs: 600,
      element: strip(
        ['SCADA'],
        42,
        'Low',
        'Hydraulic system anomaly (unspecified)',
      ),
    },
    {
      type: 'demo-element-add',
      delayMs: 600,
      element: CYCLE_TIME_TREND,
    },
    {
      type: 'demo-element-add',
      delayMs: 600,
      element: evidence(
        'evidence-scada-1',
        'SCADA',
        'Cycle time is drifting up',
        '41s avg the last two days vs 38.5s baseline (60 days) — +6.5%. Pressure variance up 47% in 5 days.',
      ),
    },
    {
      type: 'demo-element-add',
      delayMs: 800,
      element: {
        kind: 'text-block',
        id: 'closer-baler2-shallow',
        text: "The drift is real, but ambiguous. On SCADA alone I can't tell you whether it's a seal going or normal variation — could go either way. **Connect MPower, OIS, and warranty data and we'll know.**",
      },
    },
    { type: 'done', delayMs: 400, title: 'Primary Baler #2 — SCADA-only' },

    // ─────────── PAUSE — the demo's dramatic toggle moment ───────────
    { type: 'await-user-prompt', delayMs: 0 },

    // ─────────── Beat 2: connect the rest, prediction sharpens ───────────
    {
      type: 'status',
      delayMs: 200,
      message: 'Pulling in MPower, OIS, and warranty/lot data...',
    },

    // Show the live data-sources panel at the top of this turn so the
    // demoer can point at it as each module is connected below.
    {
      type: 'demo-element-add',
      delayMs: 400,
      element: strip(
        ['SCADA'],
        42,
        'Low',
        'Hydraulic system anomaly (unspecified)',
      ),
    },

    {
      type: 'demo-element-add',
      delayMs: 300,
      element: {
        kind: 'text-block',
        id: 'pm-connect-intro',
        text: 'Three sources, one click each. Read-only — nothing changes in your systems.',
      },
    },

    // The three Connect modules. Each carries the strip snapshot + evidence
    // card it should reveal when the user clicks Connect. Demoer clicks them
    // in order during the demo.
    {
      type: 'demo-element-add',
      delayMs: 500,
      element: mpowerConnector,
    },
    {
      type: 'demo-element-add',
      delayMs: 400,
      element: oisConnector,
    },
    {
      type: 'demo-element-add',
      delayMs: 400,
      element: warrantyConnector,
    },

    // Pause until the demoer has actually clicked all 3 Connect buttons.
    // Approvals + closer text only land once the connection flow is done.
    { type: 'await-connectors-connected', delayMs: 0, count: 3 },

    // Two actions — the user takes them after seeing the full picture.
    {
      type: 'approval',
      delayMs: 700,
      proposal: {
        proposalId: 'demo-rs-act2-rec-a',
        toolName: 'create_work_order',
        actionType: 'operational_routing',
        description:
          "Today, Atlanta MRF. Route 25–30% of OCC to Secondary Baler. Cycle target on Primary Baler #2 → 41s. Shrinks today's miss from ~132t to ~50t.",
      },
    },
    {
      type: 'approval',
      delayMs: 400,
      proposal: {
        proposalId: 'demo-rs-act2-rec-b',
        toolName: 'create_work_order',
        actionType: 'scheduled_maintenance',
        description:
          'Thursday May 21, 2–4 AM. Preventive seal-kit replacement (BLR-HYD-SEAL-001, 18 in stock). Assigned to Martinez J. — Senior Hydraulics, Night shift. ~$1,200 to avoid ~$50K of failure.',
      },
    },
    {
      type: 'done',
      delayMs: 400,
      title: 'Predictive maintenance — full diagnosis',
    },
  ],
  followUps: [
    {
      triggers: [
        'yes',
        'yeah',
        'yep',
        'sure',
        'ok',
        'okay',
        'do it',
        'go',
        'go ahead',
        'please',
        'network',
        'portfolio',
        'all 47',
        'national',
        'show me',
        'show',
        'scale',
        'continue',
        "what's next",
        'next',
        'patterns',
      ],
      nextScriptId: 'network-patterns',
    },
  ],
}
